import type { ApprovalRequest, ChatMessage } from "./types";
import { dedupePills } from "./hooks/useChatHelpers";

/**
 * Reconcile the on-screen transcript with a server thread snapshot (AIVA-6899).
 *
 * The server thread store is the source of truth for WHAT was said, but a large
 * part of what the user sees is only ever built client-side and is never sent to
 * the server:
 *
 *  - **Action pills.** Every model-emitted pill is a `connection_type: "frontend"`
 *    tool (`pill_office_*` / `pill_chat_*`). The backend pauses on a
 *    `frontend_tool_call` interrupt and the add-in's own `ui.pill.*` handler
 *    resolves the final label/message (see the `REFINEMENT_KINDS` map and
 *    `timeAwareLabel` in integrations/tools/outlook/handlers.ts) before emitting
 *    the button. `TurnSink.observe` only records `display_pill` events, so those
 *    pills never reach `message.pills` in the thread store.
 *  - **Approval outcomes.** `decision` and `stale` live only on the client. The
 *    resume clears `pending_interrupt` server-side, so an approved card vanishes
 *    from the snapshot entirely; a card a later turn superseded comes back
 *    looking live and clickable when it is already dead.
 *  - **Clean user bubbles.** The backend stores the message it received, which
 *    for a book-slot pill is a wall of `time_source=` / `verified_slot_id=`
 *    parameters, and for a slash command is the expanded prompt. `sentContent`
 *    records that form so the friendly bubble can be paired to it and kept.
 *  - **Email/calendar cards** (`searchResults`, `calendarSources`),
 *    **attachment badges**, and `allowedTools` / `skillKey` from the terminal
 *    `done` event — none of which the thread DTO carries.
 *
 * Replacing the render with the server snapshot therefore stripped all of that
 * the moment the task pane remounted (which Outlook does when the WebView is
 * reclaimed after the user works in another app).
 *
 * Pairing walks both transcripts in order, one-to-one, matching on `role` plus a
 * whitespace-insensitive **prefix** of the content. Prefix rather than equality
 * because an approval resume appends its continuation onto the SAME server
 * message document (`update_last_assistant_message`'s `append_content`), so one
 * server message can correspond to two rendered bubbles — the card and the
 * follow-up. Those are consumed together and their client state merged, which is
 * what keeps the greyed-out card and the post-approval pills alive.
 *
 * Pairing is deliberately conservative: a rendered bubble that prefix-matches
 * nothing is left out and the server message is used untouched, rather than
 * risking one turn's buttons being attached to another turn's bubble. Locally
 * minted bubbles with no server counterpart (Office handler failures, selection
 * nudges) simply never pair, which is correct — the server transcript owns the
 * shape of the conversation.
 */

/**
 * Presentation state that exists only in the client's render/localStorage cache
 * and can be carried over wholesale. `actionPills` and `approval` are handled
 * separately below (union / outcome resolution rather than a plain copy), and
 * `reasoning` / `guardrailCategories` / `guardrailSources` are excluded
 * because the thread DTO persists those — the server copy is authoritative.
 */
const GRAFTED_KEYS = [
  // Carried forward so the NEXT remount can still pair this bubble: the merged
  // message is what gets re-cached, and without the key the raw server copy
  // would win on the second reload.
  "sentContent",
  "searchResults",
  "searchQuerySummary",
  "searchWindow",
  "calendarSources",
  "attachedFileNames",
  "draftAttachmentNames",
  "allowedTools",
  "skillKey",
  "plan",
  "reasoningDurationMs",
  "bookedPillLabel",
  "lockedNameCandidateIds",
] as const satisfies readonly (keyof ChatMessage)[];

type GraftedKey = (typeof GRAFTED_KEYS)[number];

/** Whether a graftable field actually holds something worth carrying over. */
function isPresent(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

/** Type-safe single-field copy — keeps the loop below free of `any`/casts. */
function copyField<K extends GraftedKey>(target: ChatMessage, source: ChatMessage, key: K): void {
  target[key] = source[key];
}

/**
 * Comparison key with all whitespace removed, so a rendered bubble still pairs
 * with the persisted copy across the `"\n\n"` joiner `append_content` inserts
 * and any trailing-newline drift in the streamed text.
 */
function matchKey(text: string): string {
  return text.replace(/\s+/g, "");
}

/** What a rendered message contributes to its server counterpart's content. */
function sentKey(rendered: ChatMessage): string {
  return matchKey(rendered.sentContent ?? rendered.content);
}

interface Pairing {
  /** Index of the first rendered message folded into this server message. */
  start: number;
  /** Cursor position just past the last consumed rendered message. */
  next: number;
}

/**
 * Scan for a pairing starting at or after `from`. The consumed range is
 * contiguous: it starts at the first same-role prefix match and extends while
 * the server content keeps going — that is the appended-resume-continuation
 * case. When `exactOnly`, a candidate is accepted only if the consumed messages
 * account for the server content in full.
 */
/**
 * Absorb the messages after `start` that continue the same server content.
 *
 * This is the appended-resume-continuation case: the client rendered the turn as
 * several bubbles while the server persisted one document. Extracted from
 * `scanPairing` so each function stays within the cognitive-complexity budget.
 */
function extendPairing(
  rendered: readonly ChatMessage[],
  start: number,
  headKey: string,
  target: ChatMessage,
  wanted: string,
): Pairing & { consumed: string } {
  let consumed = headKey;
  let cursor = start + 1;
  while (consumed.length < wanted.length && cursor < rendered.length) {
    const next = rendered[cursor];
    if (next?.role !== target.role) break;
    const extended = consumed + sentKey(next);
    if (!wanted.startsWith(extended)) break;
    consumed = extended;
    cursor++;
  }
  return { start, next: cursor, consumed };
}

function scanPairing(
  rendered: readonly ChatMessage[],
  from: number,
  target: ChatMessage,
  exactOnly: boolean,
): Pairing | null {
  const wanted = matchKey(target.content);
  for (let i = from; i < rendered.length; i++) {
    const head = rendered[i];
    if (head?.role !== target.role) continue;
    const headKey = sentKey(head);
    if (!wanted.startsWith(headKey)) continue;
    // An empty head matches every server message (all strings start with ""), so
    // it may only anchor a pairing that accounts for the content in full — i.e.
    // together with the continuation that follows it. Allowing it to anchor a
    // PARTIAL match let a stray empty bubble graft its pills onto unrelated text.
    // Nothing is lost by the restriction: an approval card sitting alone in an
    // empty bubble is recovered by `restoreOrphanApprovals` instead, which also
    // anchors it correctly and resolves its staleness.
    if (!exactOnly && headKey === "") continue;
    const { next, consumed } = extendPairing(rendered, i, headKey, target, wanted);
    if (exactOnly && consumed.length !== wanted.length) continue;
    return { start: i, next };
  }
  return null;
}

/**
 * The rendered message(s) corresponding to `target`, or null when nothing pairs.
 *
 * Exact matches win. Without that preference an empty-content rendered bubble
 * would hijack the pairing, because every string starts with "" — so a stray
 * empty bubble carrying pills could graft them onto a server message that says
 * something else entirely. The partial fallback still exists for the cases that
 * genuinely need it: a `sentContent` key truncated to its first 512 chars, and a
 * continuation the client rendered but the server never persisted.
 */
function findPairing(rendered: readonly ChatMessage[], from: number, target: ChatMessage): Pairing | null {
  return scanPairing(rendered, from, target, true) ?? scanPairing(rendered, from, target, false);
}

/**
 * The approval card to render, given what the server reports and what the client
 * remembers.
 *
 * The server wins wherever it has an answer: `resolved_interrupt` now persists
 * the decision, so a card answered on another machine rehydrates correctly and a
 * stale local cache can never contradict it. The cache is the fallback for what
 * the server cannot tell us — threads answered before that field existed, and the
 * locally-stamped `stale` flag on a card a later turn abandoned. A cached card
 * with no live server-side interrupt is shown disabled rather than dropped: the
 * action is no longer pending, and silently removing it reads as lost history.
 */
function resolveApproval(server: ChatMessage, sources: readonly ChatMessage[]): ApprovalRequest | undefined {
  const cached = sources.find((s) => s.approval)?.approval;
  if (!cached) return server.approval;
  const base = server.approval;
  if (base?.decision !== undefined) return base;
  if (cached.decision !== undefined || cached.stale === true) {
    return { ...(base ?? cached), decision: cached.decision, stale: cached.stale };
  }
  return base ?? { ...cached, stale: true };
}

/** Fold the rendered messages' client-only state into their server counterpart. */
function mergeClientState(server: ChatMessage, sources: readonly ChatMessage[]): ChatMessage {
  const merged: ChatMessage = { ...server };

  for (const key of GRAFTED_KEYS) {
    if (isPresent(merged[key])) continue;
    const source = sources.find((s) => isPresent(s[key]));
    if (source) copyField(merged, source, key);
  }

  // Union rather than fill-if-empty: a turn can carry both a backend-attached
  // pill (persisted) and a model-emitted one (not), and after a resume the two
  // rendered bubbles' rows both belong on the single merged server message.
  const pills = dedupePills([
    ...(server.actionPills ?? []),
    ...sources.flatMap((s) => s.actionPills ?? []),
  ]);
  if (pills.length > 0) merged.actionPills = pills;

  const approval = resolveApproval(server, sources);
  if (approval) merged.approval = approval;

  // A bubble whose sent form differed from what was rendered keeps the rendered
  // text — the server only ever saw the machine-readable copy. Restricted to a
  // 1:1 pairing so a merged message never loses its continuation text.
  const only = sources.length === 1 ? sources[0] : undefined;
  if (only?.sentContent !== undefined) merged.content = only.content;

  return merged;
}

/**
 * Grey out approval cards a later turn already killed.
 *
 * An undecided approval that is not the newest assistant message was abandoned
 * server-side the moment the next turn ran (`agent_base._sanitise_orphan_tool_messages`
 * stubs the gated tool call), which is exactly what `runChatTurn` stamps live.
 * Deriving it from the snapshot's own shape means the card renders correctly
 * even with no local cache to graft from — a fresh device, or cleared storage.
 */
function markSupersededApprovals(server: readonly ChatMessage[]): ChatMessage[] {
  let newestAssistant = -1;
  for (let i = server.length - 1; i >= 0; i--) {
    if (server[i]?.role === "assistant") {
      newestAssistant = i;
      break;
    }
  }
  return server.map((message, i) => {
    if (!message.approval || message.approval.decision !== undefined || i === newestAssistant) return message;
    return { ...message, approval: { ...message.approval, stale: true } };
  });
}

/**
 * Re-insert approval cards the client rendered that no server message paired
 * with, so a decision the user actually made can never silently vanish.
 *
 * Pairing is content-based, and an approval turn is precisely the case where the
 * two sides' text can drift apart: the card bubble is often empty, the resume
 * appends its continuation onto the same document, and the client may have
 * substituted an empty-bubble fallback the server never saw. Rather than leave
 * the card's survival contingent on that match, an unpaired card is spliced back
 * in after the nearest preceding message that did make it through.
 *
 * Deliberately limited to approval-bearing messages. They have a stable identity
 * (`interrupt_id`) to dedupe on, and their absence is the most confusing kind of
 * gap — the user took an action and the record of it disappeared. Other unpaired
 * bubbles (Office handler failures, selection nudges) are locally minted and
 * SHOULD drop out; resurrecting those would put noise back on screen.
 *
 * Idempotent across reloads: a restored card has no server counterpart, so it
 * comes back through this same path next time and the `interrupt_id` guard keeps
 * it to exactly one.
 */
/**
 * Where to splice an orphaned card back in: the position of the nearest
 * PRECEDING rendered message that paired with the server snapshot.
 *
 * When nothing before it paired (an orphan near the top of the transcript, or a
 * run of locally-minted bubbles), fall FORWARD instead and sit just before the
 * next message that did — otherwise the card jumps to the very top of the
 * conversation, far from the turn it belongs to. Returns -1 when nothing paired
 * at all, which splices it to the front.
 */
function findOrphanAnchor(
  foldedAt: ReadonlyMap<number, number>,
  index: number,
  renderedLength: number,
): number {
  for (let j = index - 1; j >= 0; j--) {
    const at = foldedAt.get(j);
    if (at !== undefined) return at;
  }
  for (let j = index + 1; j < renderedLength; j++) {
    const at = foldedAt.get(j);
    if (at !== undefined) return at - 1;
  }
  return -1;
}

function restoreOrphanApprovals(
  out: ChatMessage[],
  rendered: readonly ChatMessage[],
  foldedAt: ReadonlyMap<number, number>,
  from = 0,
): ChatMessage[] {
  const seen = new Set(out.filter((m) => m.approval).map((m) => m.approval!.interrupt_id));
  const result = [...out];
  // Backwards, so each splice happens at or above the anchors still to be used
  // and can't shift them out from under the remaining inserts.
  for (let i = rendered.length - 1; i >= from; i--) {
    const message = rendered[i];
    const approval = message?.approval;
    if (!message || !approval || foldedAt.has(i) || seen.has(approval.interrupt_id)) continue;
    seen.add(approval.interrupt_id);
    const anchor = findOrphanAnchor(foldedAt, i, rendered.length);
    // No live interrupt paired with it, so the action is not pending: an
    // undecided orphan renders disabled rather than looking clickable.
    const resolved =
      approval.decision === undefined && approval.stale !== true
        ? { ...message, approval: { ...approval, stale: true } }
        : message;
    result.splice(anchor + 1, 0, resolved);
  }
  return result;
}

function coverageStart(pairing: Pairing | null): number | null {
  return pairing === null ? null : pairing.start;
}

function appearsInSnapshot(message: ChatMessage, snapshot: readonly ChatMessage[]): boolean {
  const key = sentKey(message);
  if (key === "") return false;
  return snapshot.some((s) => s.role === message.role && matchKey(s.content).startsWith(key));
}

function uncoveredPrefix(
  rendered: readonly ChatMessage[],
  covered: number | null,
  snapshot: readonly ChatMessage[],
): ChatMessage[] {
  if (covered === null) return [];
  const prefix = rendered.slice(0, covered).filter((m) => !appearsInSnapshot(m, snapshot));
  return prefix.some((m) => m.role === "user") ? prefix : [];
}

/**
 * Server snapshot, with the rendered transcript's client-only presentation state
 * (action pills, approval outcomes, cards, badges, clean user bubbles) folded
 * back onto the messages it belongs to, and any rendered history the snapshot
 * does not reach back to kept ahead of it.
 */
export function reconcileTranscript(
  rendered: readonly ChatMessage[],
  server: readonly ChatMessage[],
): ChatMessage[] {
  const snapshot = markSupersededApprovals(server);
  if (rendered.length === 0) return snapshot;
  const out: ChatMessage[] = [];
  /** Rendered index → index in `out` of the message it was folded into. */
  const foldedAt = new Map<number, number>();
  // Monotonic cursor into `rendered` — keeps the pairing one-to-one and in
  // order, so two turns with identical text can't both claim the same pills.
  let cursor = 0;
  const head = snapshot[0];
  const covered = head ? coverageStart(findPairing(rendered, 0, head)) : null;
  for (const message of snapshot) {
    const pairing = findPairing(rendered, cursor, message);
    if (pairing === null) {
      out.push(message);
      continue;
    }
    const at = out.length;
    out.push(mergeClientState(message, rendered.slice(pairing.start, pairing.next)));
    for (let i = pairing.start; i < pairing.next; i++) foldedAt.set(i, at);
    cursor = pairing.next;
  }
  const prefix = uncoveredPrefix(rendered, covered, snapshot);
  const reconciled = restoreOrphanApprovals(out, rendered, foldedAt, prefix.length > 0 ? (covered ?? 0) : 0);
  return prefix.length > 0 ? [...prefix, ...reconciled] : reconciled;
}
